import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
// import { DatabaseSearch } from "lucide-react";
import Git from "./Git";
import LatestNews from "./LatestNews";
import "../styles/about.css";

interface AboutItem {
  id: number;
  section_key: string;
  block_key: string;
  title: string;
  subtitle: string;
  content: string;
  image: string;
  link: string;
  sort_order: number;
  status: "active" | "inactive";
  createdAt?: string;
  updatedAt?: string;
}

const API_URL =
  import.meta.env.VITE_LYRAZONE_BASE_URL || "http://localhost:5000/api";
const IMAGE_BASE_URL =
  import.meta.env.VITE_LYRAZONE_IMAGE_URL || "http://localhost:5000/uploads";

const About: React.FC = () => {
  const [aboutItems, setAboutItems] = useState<AboutItem[]>([]);

  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/about/website`);
      const data = Array.isArray(response.data?.data) ? response.data.data : [];
      setAboutItems(
        data
          .filter((item: AboutItem) => item.status === "active")
          .sort((a: AboutItem, b: AboutItem) => a.sort_order - b.sort_order),
      );
    } catch (error) {
      console.error("Error fetching about:", error);
      setAboutItems([]);
    }
  };

  const grouped = useMemo(() => {
    return {
      home_banner: aboutItems.filter(
        (item) => item.section_key === "home_banner",
      ),
      about_p1_left: aboutItems.filter(
        (item) => item.section_key === "about_p1_left",
      ),
      about_p1_right: aboutItems.filter(
        (item) => item.section_key === "about_p1_right",
      ),
      about_p2: aboutItems.filter((item) => item.section_key === "about_p2"),
      leadership: aboutItems.filter(
        (item) => item.section_key === "leadership",
      ),
      infrastructure: aboutItems.filter(
        (item) => item.section_key === "infrastructure",
      ),
      unique_value: aboutItems.filter(
        (item) => item.section_key === "unique_value",
      ),
    };
  }, [aboutItems]);

  const getImageUrl = (image?: string) => {
    if (!image) return "";
    if (image.startsWith("http://") || image.startsWith("https://"))
      return image;
    return `${IMAGE_BASE_URL}/uploads/about/${image}`;
  };

  const renderLink = (item: AboutItem) => {
    if (!item.link) return null;

    if (item.link.startsWith("/")) {
      return <Link to={item.link}>{item.title || "Read More"}</Link>;
    }

    return (
      <a href={item.link} target="_blank" rel="noopener noreferrer">
        {item.title || "Read More"}
      </a>
    );
  };

  const leftPrimary = grouped.about_p1_left[0];
  const rightPrimary = grouped.about_p1_right[0];
  const leftSecondary = grouped.about_p1_left[1];
  const homeBanner = grouped.home_banner[0];

  return (
    <>
      <div
        id="home-p"
        className="home-p pages-head2 text-center"
        style={
          homeBanner?.image
            ? {
                backgroundImage: `url(${getImageUrl(homeBanner.image)})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }
            : undefined
        }
      >
        <div className="container">
          <h1 className="wow fadeInUp" data-wow-delay="0.1s">
            {homeBanner?.title || ""}
          </h1>

          <p>{homeBanner?.subtitle || ""}</p>

          {homeBanner?.content && (
            <div
              className="home-banner-content wow fadeInUp"
              data-wow-delay="0.2s"
              style={{ whiteSpace: "pre-line" }}
            >
              {homeBanner.content}
            </div>
          )}

          {homeBanner?.link && (
            <div className="about-readmore-wrap">{renderLink(homeBanner)}</div>
          )}
        </div>
      </div>
      <div className="about-page">
        <section id="about-p1" className="about-p1">
          <div className="container">
            <div className="row">
              <div className="col-md-6 col-sm-12">
                {leftPrimary && (
                  <div className="about-p1-left-block">
                    <div className="about-p1-left-content">
                      <h3>{leftPrimary.title}</h3>
                      <span className="about-title-underline"></span>
                      <p style={{ whiteSpace: "pre-line" }}>
                        {leftPrimary.content}
                      </p>

                      {leftPrimary.link && (
                        <div className="about-readmore-wrap">
                          {renderLink(leftPrimary)}
                        </div>
                      )}
                    </div>

                    {leftPrimary.image && (
                      <div className="about-p1-left-image-wrap">
                        <img
                          src={getImageUrl(leftPrimary.image)}
                          alt={leftPrimary.title}
                          className="img-fluid"
                        />
                      </div>
                    )}
                  </div>
                )}

                {leftSecondary && (
                  <div className="about-p1-left-block">
                    <div className="about-p1-left-content">
                      <h3>{leftSecondary.title}</h3>
                      <span className="about-title-underline"></span>
                      <p style={{ whiteSpace: "pre-line" }}>
                        {leftSecondary.content}
                      </p>

                      {leftSecondary.link && (
                        <div className="about-readmore-wrap">
                          {renderLink(leftSecondary)}
                        </div>
                      )}
                    </div>

                    {leftSecondary.image && (
                      <div className="about-p1-left-image-wrap">
                        <img
                          src={getImageUrl(leftSecondary.image)}
                          alt={leftSecondary.title}
                          className="img-fluid"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="col-md-6 col-sm-12">
                {rightPrimary && (
                  <div className="about-p1-right-block">
                    {rightPrimary.image && (
                      <div className="about-p1-right-image-wrap">
                        <img
                          src={getImageUrl(rightPrimary.image)}
                          alt={rightPrimary.title}
                          className="img-fluid"
                        />
                      </div>
                    )}

                    <div className="about-p1-right-content">
                      <h3>{rightPrimary.title}</h3>
                      <span className="about-title-underline"></span>
                      <p style={{ whiteSpace: "pre-line" }}>
                        {rightPrimary.content}
                      </p>

                      {rightPrimary.link && (
                        <div className="about-readmore-wrap">
                          {renderLink(rightPrimary)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section id="about-p2" className="about-p2">
          <div className="container">
            <div className="row">
              {grouped.about_p2.length > 0 ? (
                grouped.about_p2.map((item) => (
                  <div className="col-md-4 col-sm-6" key={item.id}>
                    <div className="about-p2-box text-center">
                      {item.title && <h4>{item.title}</h4>}
                      {item.content && <p>{item.content}</p>}
                    </div>
                  </div>
                ))
              ) : (
                <>
                  <div className="col-md-4 col-sm-6">
                    <div className="about-p2-box text-center">
                      <h4>Mission</h4>
                      <p>Deliver practical and scalable digital solutions.</p>
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <div className="about-p2-box text-center">
                      <h4>Vision</h4>
                      <p>Empower businesses through modern innovation.</p>
                    </div>
                  </div>
                  <div className="col-md-4 col-sm-6">
                    <div className="about-p2-box text-center">
                      <h4>Values</h4>
                      <p>Integrity, quality, and customer-focused delivery.</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        <section id="leadership" className="leadership-section">
          <div className="container">
            <div className="section-title text-center">
              <h2>Leadership</h2>
            </div>
            <div className="row">
              {grouped.leadership.length > 0 ? (
                grouped.leadership.map((item) => (
                  <div className="col-md-6 col-sm-6" key={item.id}>
                    <div className="leadership-card text-center">
                      {item.image && (
                        <img
                          src={getImageUrl(item.image)}
                          alt={item.title}
                          className="img-fluid"
                        />
                      )}
                      {item.title && <h4>{item.title}</h4>}
                      {item.subtitle && <h6>{item.subtitle}</h6>}
                      {item.content && <p>{item.content}</p>}
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-md-12 text-center">
                  <p>Leadership details will appear here.</p>
                </div>
              )}
            </div>
          </div>
        </section>

        <section id="infrastructure" className="infrastructure-section">
          <div className="container">
            <div className="section-title text-center">
              <h2>Infrastructure</h2>
            </div>
            <div className="row">
              {grouped.infrastructure.length > 0 ? (
                grouped.infrastructure.map((item) => (
                  <div className="col-md-4 col-sm-6" key={item.id}>
                    <div className="infrastructure-card">
                      {item.image && (
                        <img
                          src={getImageUrl(item.image)}
                          alt={item.title}
                          className="img-fluid"
                        />
                      )}
                      {item.title && <h4>{item.title}</h4>}
                      {item.content && <p>{item.content}</p>}
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-md-12 text-center">
                  <p>Infrastructure details will appear here.</p>
                </div>
              )}
            </div>
          </div>
        </section>

        <section id="unique-value" className="unique-value-section">
          <div className="container">
            <div className="section-title text-center">
              <h2>Why Choose Us</h2>
            </div>
            <div className="row">
              {grouped.unique_value.length > 0 ? (
                grouped.unique_value.map((item) => (
                  <div className="col-md-4 col-sm-6" key={item.id}>
                    <div className="unique-value-card">
                      {item.image && (
                        <img
                          src={getImageUrl(item.image)}
                          alt={item.title}
                          className="img-fluid"
                        />
                      )}
                      {item.title && <h4>{item.title}</h4>}
                      {item.content && <p>{item.content}</p>}
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-md-12 text-center">
                  <p>Unique value details will appear here.</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      <Git />
      <LatestNews />
    </>
  );
};

export default About;
